import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinks = [
    { label: t('nav.home'), section: 'home' },
    { label: t('nav.services'), section: 'services' },
    { label: t('nav.projects'), section: 'projects' },
    { label: t('nav.about'), section: 'about' },
    { label: t('nav.contact'), section: 'contact' },
  ];

  const servicesList = t('footer.servicesList');

  return (
    <footer className="bg-black border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <img
              src="https://customer-assets.emergentagent.com/job_734cb631-c213-4223-9ec5-3c4d6675302f/artifacts/573hpmih_Transparent-PNG.png"
              alt="Martinez Solutions"
              className="h-16 mb-8"
            />
            <p className="font-raleway font-light text-sm text-white/40 leading-relaxed tracking-wide">
              {t('footer.description')}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="font-montserrat font-light text-xs tracking-[0.25em] text-[#C5A55C] uppercase mb-8">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-4">
              {navLinks.map((item) => (
                <li key={item.section}>
                  <button
                    onClick={() => scrollToSection(item.section)}
                    className="font-raleway font-light text-sm text-white/40 hover:text-[#C5A55C] transition-colors duration-300 tracking-wide"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-montserrat font-light text-xs tracking-[0.25em] text-[#C5A55C] uppercase mb-8">
              {t('footer.services')}
            </h3>
            <ul className="space-y-4">
              {servicesList.map((item, index) => (
                <li key={index}>
                  <span className="font-raleway font-light text-sm text-white/40 tracking-wide">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-montserrat font-light text-xs tracking-[0.25em] text-[#C5A55C] uppercase mb-8">
              {t('footer.contactInfo')}
            </h3>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <Phone className="w-4 h-4 text-[#C5A55C] mt-1 stroke-1" />
                <a href="tel:+12155550123" className="font-raleway font-light text-sm text-white/40 hover:text-white transition-colors tracking-wide">
                  (215) 555-0123
                </a>
              </li>
              <li className="flex items-start gap-4">
                <Mail className="w-4 h-4 text-[#C5A55C] mt-1 stroke-1" />
                <a href="mailto:info@martinez-solutions.com" className="font-raleway font-light text-sm text-white/40 hover:text-white transition-colors tracking-wide">
                  info@martinez-solutions.com
                </a>
              </li>
              <li className="flex items-start gap-4">
                <MapPin className="w-4 h-4 text-[#C5A55C] mt-1 stroke-1" />
                <span className="font-raleway font-light text-sm text-white/40 tracking-wide">
                  Philadelphia, PA
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-raleway font-light text-xs text-white/30 tracking-wide">
            © {currentYear} Martinez Solutions. {t('footer.rights')}
          </p>
          <p className="font-raleway font-light text-xs text-white/20 tracking-wide">
            {t('footer.tagline')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
